/**
 * useCollapse — Jun-2026 (User-Wunsch).
 *
 * Merkt den Ein-/Ausklapp-Zustand von Cockpit-Statistik-Widgets
 * GERÄTEÜBERGREIFEND: Initialwert kommt aus localStorage (gerätelokal,
 * sofort) bzw. dem serverseitigen `DealerProfile.dashboard_collapsed`
 * (geräteübergreifend, beim ersten Öffnen auf einem neuen Gerät). Beim
 * Umschalten wird der Wert in localStorage gesetzt UND best-effort per
 * PATCH /users/me/dealer-profile persistiert.
 *
 * Ein modul-weiter, pro-User gepflegter Cache stellt sicher, dass mehrere
 * Toggles in derselben Sitzung zusammengeführt werden (kein Überschreiben).
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./auth";
import api from "./api";

let cacheUserId = null;
let cache = {};

// Jul-2026 (User-Wunsch) — Das Cockpit startet in JEDER neuen Browser-Sitzung
// mit EINGEKLAPPTEN Bereichen. Gespeicherte Aufklapp-Zustände gelten nur
// innerhalb der laufenden Sitzung (Toggles funktionieren weiterhin normal).
const FRESH_SESSION = (() => {
    try {
        if (sessionStorage.getItem("tw_collapse_session")) return false;
        sessionStorage.setItem("tw_collapse_session", "1");
        Object.keys(localStorage)
            .filter((k) => k.startsWith("tw_collapse_"))
            .forEach((k) => localStorage.removeItem(k));
        return true;
    } catch { return false; }
})();

// Jun-2026 (User-Wunsch) — Registry aller aktuell sichtbaren einklappbaren
// Widgets, damit „Alle auf-/einklappen" alle Widgets des aktiven Bereichs auf
// einmal umschalten kann (im Cockpit ist immer nur EIN Bereich gemountet).
const listeners = new Set();

export function setAllCollapsed(value) {
    listeners.forEach((entry) => {
        localStorage.setItem(`tw_collapse_${entry.key}`, value ? "1" : "0");
        cache = { ...cache, [entry.key]: value };
        entry.set(value);
    });
    api.patch("/users/me/dealer-profile", { dashboard_collapsed: { ...cache } }).catch(() => { /* fail-soft */ });
}

function syncCache(user) {
    if (user?.id && cacheUserId !== user.id) {
        cacheUserId = user.id;
        cache = { ...(user.dashboard_collapsed || {}) };
    }
    return cache;
}

// Jun-2026 (User-Wunsch) — Sortier-/Filter-/Blockgrößen-Auswahl je Ankauf-Liste
// GERÄTEÜBERGREIFEND merken. Initialwert: localStorage (gerätelokal, sofort) →
// sonst serverseitiges `DealerProfile.dashboard_prefs` (beim ersten Öffnen auf
// einem neuen Gerät). Änderungen werden lokal gespeichert UND best-effort per
// PATCH persistiert. Ein modul-weiter Cache führt mehrere Listen zusammen.
let prefsCacheUserId = null;
let prefsCache = {};

function syncPrefsCache(user) {
    if (user?.id && prefsCacheUserId !== user.id) {
        prefsCacheUserId = user.id;
        prefsCache = { ...(user.dashboard_prefs || {}) };
    }
    return prefsCache;
}

export function useListPrefs(key, defaults) {
    const { user } = useAuth();
    syncPrefsCache(user);
    const storageKey = `tw_listprefs_${key}`;
    const [prefs, setPrefs] = useState(() => {
        try {
            const ls = localStorage.getItem(storageKey);
            if (ls) return { ...defaults, ...JSON.parse(ls) };
        } catch { /* ignore */ }
        const sv = user?.dashboard_prefs?.[key];
        if (sv && typeof sv === "object") return { ...defaults, ...sv };
        return { ...defaults };
    });
    const update = useCallback((patch) => {
        setPrefs((prev) => {
            const next = { ...prev, ...patch };
            try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
            prefsCache = { ...prefsCache, [key]: next };
            api.patch("/users/me/dealer-profile", { dashboard_prefs: { ...prefsCache } }).catch(() => { /* fail-soft */ });
            return next;
        });
    }, [key, storageKey]);
    return [prefs, update];
}

export function useCollapse(key, defaultCollapsed = false) {
    const { user } = useAuth();
    syncCache(user);
    const [collapsed, setCollapsed] = useState(() => {
        const v = localStorage.getItem(`tw_collapse_${key}`);
        if (v === "1") return true;
        if (v === "0") return false;
        if (FRESH_SESSION) return true; // Jul-2026: neue Sitzung → immer eingeklappt starten
        const sv = user?.dashboard_collapsed?.[key];
        if (typeof sv === "boolean") return sv;
        return defaultCollapsed;
    });
    const toggle = useCallback(() => {
        setCollapsed((c) => {
            const next = !c;
            localStorage.setItem(`tw_collapse_${key}`, next ? "1" : "0");
            cache = { ...cache, [key]: next };
            api.patch("/users/me/dealer-profile", { dashboard_collapsed: { ...cache } }).catch(() => { /* offline/fail-soft */ });
            return next;
        });
    }, [key]);

    // Für „Alle auf-/einklappen": diese Instanz registrieren.
    useEffect(() => {
        const entry = { key, set: setCollapsed };
        listeners.add(entry);
        return () => { listeners.delete(entry); };
    }, [key]);

    return [collapsed, toggle];
}
